"use client";

import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useAction } from "convex/react";
import { useLocale, useTranslations } from "next-intl";
import * as Sentry from "@sentry/nextjs";
import { Link, useRouter } from "@/i18n/navigation";
import posthog from "posthog-js";
import { api } from "../../../convex/_generated/api";
import { AuthCard } from "./auth-card";
import { AuthInput } from "./auth-input";
import { AuthButton } from "./auth-button";
import { OAuthButtons } from "./oauth-buttons";
import { OrDivider } from "./or-divider";
import { LegalFooter } from "./legal-footer";
import { EmailInUseNotice } from "./email-in-use-notice";
import { errorCode } from "./error-mapping";
import { readUtmArgs } from "./session-helpers";

const VERIFY_EMAIL_KEY = "mint24_verify_email";

export function SignUpForm() {
  const t = useTranslations("auth");
  const router = useRouter();
  const locale = useLocale();
  const registerStart = useAction(api.emailAuth.registerStart);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const noticeRef = useRef<HTMLDivElement | null>(null);

  const emailInUseProvider =
    errorKey === "errors.emailInUseGoogle"
      ? "google"
      : errorKey === "errors.emailInUseApple"
        ? "apple"
        : null;

  useEffect(() => {
    if (emailInUseProvider && noticeRef.current) {
      noticeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [emailInUseProvider]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErrorKey(null);
    const trimmedEmail = email.trim().toLowerCase();
    posthog.capture("sign_up_submitted", { auth_provider: "email" });
    try {
      // Race registerStart against a 20s timeout so a stuck Convex socket
      // surfaces as an error instead of a permanent spinner.
      await Promise.race([
        registerStart({
          email: trimmedEmail,
          name: name.trim() || undefined,
          password,
          locale,
          ...readUtmArgs(),
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("registerStart timeout")), 20_000),
        ),
      ]);
      posthog.capture("otp_sent", { auth_provider: "email" });
      try {
        sessionStorage.setItem(VERIFY_EMAIL_KEY, trimmedEmail);
      } catch {}
      router.push(`/verify-otp?email=${encodeURIComponent(trimmedEmail)}`);
    } catch (err) {
      const code = errorCode(err);
      setErrorKey(code);
      posthog.capture("sign_up_failed", { auth_provider: "email", error_code: code });
      if (code === "errors.generic") {
        Sentry.captureException(err, { tags: { feature: "email_signup" } });
      }
      setLoading(false);
    }
  }

  return (
    <AuthCard
      title={t("signUp.title")}
      subtitle={
        <>
          {t("signUp.subtitleBefore")}&nbsp;&nbsp;&nbsp;
          <Link href="/sign-in" className="font-medium text-ink underline underline-offset-4 decoration-ink/30 hover:decoration-ink">
            {t("signUp.subtitleLink")}
          </Link>
        </>
      }
      footer={<LegalFooter />}
    >
      {emailInUseProvider && (
        <div ref={noticeRef}>
          <EmailInUseNotice provider={emailInUseProvider} email={email.trim().toLowerCase()} />
        </div>
      )}

      {process.env.NEXT_PUBLIC_OAUTH_ENABLED !== "false" && (
        <>
          <OAuthButtons source="sign_up" />
          <OrDivider />
        </>
      )}

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <AuthInput
          label={t("signUp.emailLabel")}
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder={t("signUp.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <AuthInput
          label={t("signUp.passwordLabel")}
          name="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          placeholder={t("signUp.passwordPlaceholder")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {errorKey && !emailInUseProvider && (
          <p className="text-sm text-red-400 text-center">{t(errorKey)}</p>
        )}
        <AuthButton type="submit" loading={loading} className="mt-2">
          {t("signUp.submit")}
        </AuthButton>
      </form>
    </AuthCard>
  );
}
